import { CHANNELS, type CreateChannelConnection } from '@sbc/domain-models/gateway';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CHANNEL_CREDENTIALS, CREDENTIAL_LABELS } from '@/routes/gateway/channel-credentials';

const CHANNEL_LABELS: Record<(typeof CHANNELS)[number], string> = {
  instagram: 'Instagram',
  facebook: 'Facebook Page',
  intercom: 'Intercom',
  slack: 'Slack',
  sbc: 'SBC app',
  tiktok: 'TikTok',
  facebook_group: 'Facebook group',
  quo: 'Quo (SMS)',
  whatsapp: 'WhatsApp',
  klaviyo_sms: 'Klaviyo SMS',
};

// Non-secret fields prefilled on edit; the secret fields are always blank (write-only).
export interface ConnectionFormValues {
  channel: (typeof CHANNELS)[number];
  externalAccountId: string;
  externalActorId: string;
  handle: string;
  oauthClientId: string;
  enabled: boolean;
  inboxEnabled: boolean;
}

export interface SecretState {
  verifyTokenSet: boolean;
  appSecretSet: boolean;
  accessTokenSet: boolean;
  oauthClientSecretSet: boolean;
}

const EMPTY: ConnectionFormValues = {
  channel: CHANNELS[0],
  externalAccountId: '',
  externalActorId: '',
  handle: '',
  oauthClientId: '',
  enabled: true,
  inboxEnabled: false,
};

interface ConnectionFormProps {
  initial?: ConnectionFormValues;
  // On edit, indicates which secrets are already stored (so a blank field means "unchanged").
  secretState?: SecretState;
  submitting: boolean;
  submitLabel: string;
  error?: string | null;
  onSubmit: (input: CreateChannelConnection) => void;
  onCancel: () => void;
}

// What `external_account_id` means differs per channel, and getting it wrong is not a
// validation error — it is a connection that saves cleanly and then talks to the wrong
// place. Keyed off the channel tuple so adding a channel forces this decision too.
const ACCOUNT_ID_COPY: Record<(typeof CHANNELS)[number], { hint: string; placeholder: string }> = {
  instagram: {
    hint: 'The business account id the webhook payload carries. Unique per channel.',
    placeholder: '17841400000000000',
  },
  facebook: {
    hint: 'The Facebook Page id the webhook payload carries. The stored token is the system-user token; the Page token is exchanged from it at call time.',
    placeholder: '101065388703953',
  },
  intercom: {
    hint: 'The Intercom workspace id. Unique per channel.',
    placeholder: 'm2a1uum4',
  },
  slack: {
    hint: "The Slack app's id, from its Basic Information page. Slack delivers this app's events to /api/gateway/webhooks/slack/<app id>/events and its shortcut clicks to /api/gateway/webhooks/slack/<app id>/interactions. The investigation announcement connection is the one exception: it holds the id of the one channel it posts into (Channel details → bottom, not the name) and no signing secret.",
    placeholder: 'A0123ABCDEF',
  },
  sbc: {
    hint: 'The SBC environment this connection speaks for. There is no external account to name — jobs arrive on the platform-wide S2S token, and the agent is named per job.',
    placeholder: 'staging',
  },
  tiktok: {
    hint: 'The TikTok open_id. "Connect TikTok account" on the connections page fills it in and stores the tokens; there is nothing to paste here.',
    placeholder: '-000abcDEF123',
  },
  facebook_group: {
    hint: 'The numeric group id from the group URL (facebook.com/groups/<id>). There are no credentials: the bot reads and replies through a logged-in browser, which an admin logs in from the live view on the connection page.',
    placeholder: '2103500403477738',
  },
  quo: {
    hint: "The Quo phone number id (starts with PN), from Quo's API settings. Point the Quo webhook at /api/gateway/webhooks/quo and paste its signing key below; the API key is the one replies are sent with.",
    placeholder: 'PNabc123',
  },
  whatsapp: {
    hint: 'The WhatsApp phone number id from WhatsApp Manager (not the phone number). The app secret is the Meta app that owns the WhatsApp webhook, and the token is its system-user token. Meta calls /api/gateway/webhooks/whatsapp.',
    placeholder: '973441862529715',
  },
  klaviyo_sms: {
    hint: 'The Klaviyo public account id (site id), from Settings → Account. The access token is a private API key with events, metrics and profiles read access plus conversations read and write.',
    placeholder: 'AbC123',
  },
};

const HANDLE_PLACEHOLDER: Record<(typeof CHANNELS)[number], string> = {
  instagram: '@asksbc',
  facebook: 'Seoul Beauty Club',
  intercom: 'SBC support',
  slack: '@translator',
  sbc: 'SBC staging',
  tiktok: '@seoulbeautyclub',
  facebook_group: 'SBC Insiders',
  quo: '+1 555 010 0100',
  whatsapp: 'SBC WhatsApp',
  klaviyo_sms: 'SBC SMS',
};

const INBOX_CHANNELS: ReadonlySet<(typeof CHANNELS)[number]> = new Set([
  'instagram',
  'facebook',
  'whatsapp',
  'quo',
  'klaviyo_sms',
  'facebook_group',
  'tiktok',
]);

function secretHint(isEdit: boolean, isSet: boolean): string {
  if (!isEdit) {
    return 'Stored encrypted; never shown again.';
  }
  return isSet ? 'Configured. Leave blank to keep the current value.' : 'Not configured yet.';
}

export function ConnectionForm({
  initial,
  secretState,
  submitting,
  submitLabel,
  error,
  onSubmit,
  onCancel,
}: ConnectionFormProps) {
  const isEdit = initial !== undefined;
  const [values, setValues] = useState<ConnectionFormValues>(initial ?? EMPTY);
  const [webhookVerifyToken, setVerifyToken] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [oauthClientSecret, setOauthClientSecret] = useState('');

  // Showing a channel the credentials it cannot use invites an operator to fill in something
  // meaningless, so each field is gated on the channel that actually reads it — from the same
  // map the connection page's rows read, so a field the form hides can never be reported there.
  const credentials = CHANNEL_CREDENTIALS[values.channel];
  const hasInbox = INBOX_CHANNELS.has(values.channel);
  const isIntercom = values.channel === 'intercom';
  const isSlack = values.channel === 'slack';
  const labels = CREDENTIAL_LABELS[values.channel];

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({
      ...values,
      // Forced null when the channel has no actor: the field is hidden, not cleared, so a
      // value typed for Intercom would otherwise ride along to a Slack connection.
      externalActorId: credentials.actor ? values.externalActorId.trim() || null : null,
      // Omitted for a channel with no OAuth client, so its stored value is never touched.
      oauthClientId: credentials.oauthClient ? values.oauthClientId.trim() || null : undefined,
      oauthClientSecret: credentials.oauthClient
        ? oauthClientSecret.trim() || undefined
        : undefined,
      // Blank secret field ⇒ omit ⇒ leave unchanged (edit) / unset (create).
      webhookVerifyToken: webhookVerifyToken.trim() || undefined,
      appSecret: appSecret.trim() || undefined,
      accessToken: accessToken.trim() || undefined,
    });
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label htmlFor="channel">Channel</Label>
        <Select
          value={values.channel}
          onValueChange={(value) =>
            setValues((prev) => ({
              ...prev,
              channel: CHANNELS.find((c) => c === value) ?? prev.channel,
            }))
          }
        >
          <SelectTrigger id="channel">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CHANNELS.map((channel) => (
              <SelectItem key={channel} value={channel}>
                {CHANNEL_LABELS[channel]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="handle">Handle</Label>
        <Input
          id="handle"
          autoComplete="off"
          required
          maxLength={120}
          placeholder={HANDLE_PLACEHOLDER[values.channel]}
          value={values.handle}
          onChange={(event) => setValues((prev) => ({ ...prev, handle: event.target.value }))}
        />
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Display label for this account.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="externalAccountId">{labels.account}</Label>
        <Input
          id="externalAccountId"
          autoComplete="off"
          required
          maxLength={120}
          placeholder={ACCOUNT_ID_COPY[values.channel].placeholder}
          value={values.externalAccountId}
          onChange={(event) =>
            setValues((prev) => ({ ...prev, externalAccountId: event.target.value }))
          }
        />
        <p className="text-xs text-[var(--color-muted-foreground)]">
          {ACCOUNT_ID_COPY[values.channel].hint}
        </p>
      </div>

      {credentials.actor && (
        <div className="space-y-2">
          <Label htmlFor="externalActorId">{labels.actor}</Label>
          <Input
            id="externalActorId"
            autoComplete="off"
            maxLength={120}
            placeholder={isSlack ? 'U0123ABCDEF' : '5812345'}
            value={values.externalActorId}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, externalActorId: event.target.value }))
            }
          />
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {isSlack
              ? "The app's bot user id, which is how a mention of the agent is recognised. Not used on the announcement connection."
              : "The Intercom admin id the platform's internal notes are authored by."}
          </p>
        </div>
      )}

      {credentials.verifyToken && (
        <div className="space-y-2">
          <Label htmlFor="webhookVerifyToken">Webhook verify token</Label>
          <PasswordInput
            id="webhookVerifyToken"
            autoComplete="new-password"
            placeholder={
              isEdit && secretState?.verifyTokenSet
                ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'
                : ''
            }
            value={webhookVerifyToken}
            onChange={(event) => setVerifyToken(event.target.value)}
          />
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {secretHint(isEdit, secretState?.verifyTokenSet ?? false)}
          </p>
        </div>
      )}

      {credentials.appSecret && (
        <div className="space-y-2">
          <Label htmlFor="appSecret">{labels.appSecret}</Label>
          <PasswordInput
            id="appSecret"
            autoComplete="new-password"
            placeholder={
              isEdit && secretState?.appSecretSet
                ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'
                : ''
            }
            value={appSecret}
            onChange={(event) => setAppSecret(event.target.value)}
          />
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {isIntercom
              ? `The client secret of the platform's own Intercom app — the key its webhook signatures are verified against. Not SBC's app secret. ${secretHint(isEdit, secretState?.appSecretSet ?? false)}`
              : isSlack
                ? `From the app's Basic Information page; every event is verified against it. Leave it empty on the announcement connection only. ${secretHint(isEdit, secretState?.appSecretSet ?? false)}`
                : secretHint(isEdit, secretState?.appSecretSet ?? false)}
          </p>
        </div>
      )}

      {credentials.accessToken && (
        <div className="space-y-2">
          <Label htmlFor="accessToken">{labels.accessToken}</Label>
          <PasswordInput
            id="accessToken"
            autoComplete="new-password"
            placeholder={isEdit && secretState?.accessTokenSet ? '••••••••' : ''}
            value={accessToken}
            onChange={(event) => setAccessToken(event.target.value)}
          />
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {secretHint(isEdit, secretState?.accessTokenSet ?? false)}
          </p>
        </div>
      )}

      {credentials.oauthClient && (
        <>
          <div className="space-y-2">
            <Label htmlFor="oauthClientId">OAuth client id</Label>
            <Input
              id="oauthClientId"
              autoComplete="off"
              maxLength={120}
              placeholder="1234567890.1234567890123"
              value={values.oauthClientId}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, oauthClientId: event.target.value }))
              }
            />
            <p className="text-xs text-[var(--color-muted-foreground)]">
              From the app's Basic Information page. With the client secret, it lets a teammate
              connect once so translations are written under their own messages instead of in a
              thread.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="oauthClientSecret">OAuth client secret</Label>
            <PasswordInput
              id="oauthClientSecret"
              autoComplete="new-password"
              placeholder={isEdit && secretState?.oauthClientSecretSet ? '••••••••' : ''}
              value={oauthClientSecret}
              onChange={(event) => setOauthClientSecret(event.target.value)}
            />
            <p className="text-xs text-[var(--color-muted-foreground)]">
              {secretHint(isEdit, secretState?.oauthClientSecretSet ?? false)}
            </p>
          </div>
        </>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4"
          checked={values.enabled}
          onChange={(event) => setValues((prev) => ({ ...prev, enabled: event.target.checked }))}
        />
        Enabled
      </label>

      {hasInbox && (
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4"
              checked={values.inboxEnabled}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, inboxEnabled: event.target.checked }))
              }
            />
            Inbox
          </label>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Record every inbound message on this connection for manual reply from the Inbox.
          </p>
        </div>
      )}

      {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
